import { createSelector, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { endOfWeek, format, startOfWeek } from "date-fns";

import { RootState } from "app/store";
import {
  EVENT_TYPE_APPOINTMENT,
  QUICK_FILTER_DEFAULT_TYPE
} from "constants/appointments";
import { AssignedTag } from "services/types/assigned-tag.type";
import { type EventSearchOption } from "services/types/events.type";
import { MinimalStaffMember, StaffMember } from "services/types/staff.type";
import { Tag } from "services/types/tags.type";
import { formatLocalDateOnly, parseBackendDateOnly } from "utils/common";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

export type SchedulerView = "Day" | "Week" | "Work Week" | "Month";

type FilterState<T> = {
  values: T[];
  isReady: boolean;
};

interface AppointmentFiltersState {
  eventType: string;
  quickFilterCode: string;
  clinics: FilterState<number>;
  appointmentTypes: FilterState<number>;
  staff: FilterState<MinimalStaffMember | StaffMember>;
  selectedFromAvailableStaff: (MinimalStaffMember | StaffMember)[];
  excludePersonalEvents: boolean;
  showFromAllLocations: boolean;
  previewVisible: boolean;
  scheduleTooltip: EventSearchOption | null;
  editRecurringEventType: string;
  filtersPanel: {
    visible: boolean;
    animationInProgress: string;
  };
  formOpen: boolean;
  openFormId: string | null | undefined;
  patientCreateAppointmentMode: {
    formCollapsed: boolean;
    patient?: any;
    returnToPath?: string;
  };
  rescheduling: {
    formCollapsed: boolean;
    originalAppointmentData?: any;
    rescheduledAppointmentData?: any;
    returnToPath?: string;
    carriedAssignedTags?: AssignedTag[];
  };
  followUpScheduling: {
    active: boolean;
    completedAppointmentId?: number;
    patient?: any;
    appointmentType?: any;
  };
  isClassicViewMode: boolean;
  workShiftViewMode: boolean;
  raiseOnHoverViewMode: boolean;
  isShowByClinicViewMode: boolean;
  includeSupervisingProvider: boolean;
  showChangeColumnsOrderModal: boolean;
  providersOrder: { id: number; index: number }[];
  dateRange: {
    selectedDate: string;
    fromDate: string;
    toDate: string;
    currentViewName: SchedulerView;
  };
  appointmentTags: Tag[];
  staffCellData: {
    staffCount: number;
    clinicsCount: number;
    staffCellIndexes: Record<string, number>;
  };
  schedulerCellContainerWidth: number;
}

const providersOrderModeStorage = setupLocalStorageAPI(
  "schedulingProvidersOrder"
);
const scheduleViewModeStorage = setupLocalStorageAPI("scheduleViewMode");
const workShiftViewModeStorage = setupLocalStorageAPI("workShiftViewMode");
export const currentViewNameStorage = setupLocalStorageAPI("currentViewName");

const filtersPanelVisibleStorage = setupLocalStorageAPI("filtersPanelVisible");

const scheduleShowByClinicViewModeStorage = setupLocalStorageAPI(
  "scheduleShowByClinicViewMode"
);

const scheduleRaiseOnHoverStorage = setupLocalStorageAPI(
  "scheduleRaiseOnHover"
);

const scheduleIncludeSupervisingProviderStorage = setupLocalStorageAPI(
  "scheduleIncludeSupervisingProvider"
);

const initialState = {
  eventType: EVENT_TYPE_APPOINTMENT,
  quickFilterCode: QUICK_FILTER_DEFAULT_TYPE,
  clinics: { values: [], isReady: false },
  appointmentTypes: { values: [], isReady: false },
  staff: { values: [], isReady: false },
  selectedFromAvailableStaff: [],
  excludePersonalEvents: true,
  showFromAllLocations: true,
  previewVisible: false,
  scheduleTooltip: null,
  editRecurringEventType: "",
  filtersPanel: {
    visible: filtersPanelVisibleStorage.getData(true),
    animationInProgress: ""
  },
  formOpen: false,
  openFormId: null,
  rescheduling: {
    formCollapsed: false,
    originalAppointmentData: null,
    rescheduledAppointmentData: null,
    returnToPath: undefined,
    carriedAssignedTags: []
  },
  patientCreateAppointmentMode: {
    formCollapsed: false,
    patient: undefined,
    returnToPath: undefined
  },
  followUpScheduling: {
    active: false,
    completedAppointmentId: undefined,
    patient: undefined,
    appointmentType: undefined
  },
  isClassicViewMode: scheduleViewModeStorage.getData(false),
  workShiftViewMode: workShiftViewModeStorage.getData(false),
  isShowByClinicViewMode: scheduleShowByClinicViewModeStorage.getData(false),
  raiseOnHoverViewMode: scheduleRaiseOnHoverStorage.getData(false),
  includeSupervisingProvider:
    scheduleIncludeSupervisingProviderStorage.getData(false),
  showChangeColumnsOrderModal: false,
  providersOrder: providersOrderModeStorage.getData([]),
  dateRange: {
    selectedDate: format(new Date(), "yyyy-MM-dd"),
    fromDate: format(startOfWeek(new Date()), "yyyy-MM-dd"),
    toDate: format(endOfWeek(new Date()), "yyyy-MM-dd"),
    currentViewName: currentViewNameStorage.getData("Work Week")
  },
  appointmentTags: [],
  staffCellData: {
    staffCount: 0,
    clinicsCount: 0,
    staffCellIndexes: {}
  },
  schedulerCellContainerWidth: 0
} as AppointmentFiltersState;

const schedulerSlice = createSlice({
  name: "scheduler",
  initialState,
  reducers: {
    toggleFiltersPanel(state) {
      const newValue = !state.filtersPanel.visible;
      state.filtersPanel.visible = newValue;
      filtersPanelVisibleStorage.updateData(newValue);
      state.filtersPanel.animationInProgress = newValue ? "show" : "hide";
    },
    hideFiltersPanel(state) {
      const newValue = false;
      state.filtersPanel.visible = newValue;
      filtersPanelVisibleStorage.updateData(newValue);
      state.filtersPanel.animationInProgress = "";
    },
    filtersPanelStopAnimation(state) {
      state.filtersPanel.animationInProgress = "";
    },
    setEditRecurringEventType(state, action: PayloadAction<string>) {
      state.editRecurringEventType = action.payload;
    },
    showPreview(state) {
      state.previewVisible = true;
    },
    hidePreview(state) {
      state.previewVisible = false;
      state.scheduleTooltip = null;
    },
    openForm(state, action: PayloadAction<string>) {
      state.formOpen = true;
      state.rescheduling.formCollapsed = false;
      state.patientCreateAppointmentMode.formCollapsed = false;
      if (action && action.payload) {
        state.openFormId = action.payload;
      }
    },
    closeForm(state) {
      state.formOpen = false;
      state.rescheduling.formCollapsed = true;
      state.patientCreateAppointmentMode.formCollapsed = true;
      state.openFormId = null;
    },
    collapseReschedulingForm(state) {
      state.rescheduling.formCollapsed = true;
      state.previewVisible = false;
      state.formOpen = false;
    },
    expandReschedulingForm(state) {
      state.rescheduling.formCollapsed = false;
      state.previewVisible = false;
      state.formOpen = true;
    },
    updateReschedulingReturnToPath(state, action: PayloadAction<any>) {
      state.rescheduling.returnToPath = action.payload;
    },
    startRescheduling(state, action: PayloadAction<any>) {
      state.rescheduling = {
        formCollapsed: true,
        originalAppointmentData: action.payload,
        rescheduledAppointmentData: action.payload,
        carriedAssignedTags: action.payload?.assignedTags ?? []
      };
      state.patientCreateAppointmentMode = {
        formCollapsed: true,
        patient: undefined,
        returnToPath: undefined
      };
      state.previewVisible = false;
      state.formOpen = false;
    },
    updateReschedulingData(state, action: PayloadAction<any>) {
      state.rescheduling.rescheduledAppointmentData = action.payload;
    },
    removeCarriedReschedulingTag(state, action: PayloadAction<number>) {
      state.rescheduling.carriedAssignedTags = (
        state.rescheduling.carriedAssignedTags ?? []
      ).filter((assignedTag) => assignedTag.id !== action.payload);
    },
    endRescheduling(state) {
      state.rescheduling = {
        formCollapsed: true,
        originalAppointmentData: null,
        rescheduledAppointmentData: null,
        returnToPath: "",
        carriedAssignedTags: []
      };
      state.patientCreateAppointmentMode = {
        formCollapsed: true,
        patient: undefined,
        returnToPath: undefined
      };
      state.formOpen = false;
    },
    //
    collapsePatientModeForm(state) {
      state.rescheduling.formCollapsed = true;
      state.patientCreateAppointmentMode.formCollapsed = true;
      state.previewVisible = false;
      state.formOpen = false;
    },
    expandPatientModeForm(state) {
      state.patientCreateAppointmentMode.formCollapsed = true;
      state.previewVisible = false;
      state.formOpen = true;
    },
    startPatientCreateAppointmentMode(
      state,
      action: PayloadAction<{
        patient: any;
        returnToPath: string;
      }>
    ) {
      state.patientCreateAppointmentMode = {
        formCollapsed: true,
        patient: action.payload.patient,
        returnToPath: action.payload.returnToPath
      };
      state.previewVisible = false;
      state.formOpen = false;
    },
    endPatientCreateAppointmentMode(state) {
      state.rescheduling = {
        formCollapsed: true,
        originalAppointmentData: null,
        rescheduledAppointmentData: null,
        carriedAssignedTags: []
      };
      state.patientCreateAppointmentMode = {
        formCollapsed: true,
        patient: undefined,
        returnToPath: undefined
      };
      state.formOpen = false;
    },
    // Follow-up scheduling actions
    startFollowUpScheduling(
      state,
      action: PayloadAction<{
        completedAppointmentId: number;
        patient: any;
        appointmentType: any;
      }>
    ) {
      state.followUpScheduling = {
        active: true,
        completedAppointmentId: action.payload.completedAppointmentId,
        patient: action.payload.patient,
        appointmentType: action.payload.appointmentType
      };
    },
    endFollowUpScheduling(state) {
      state.followUpScheduling = {
        active: false,
        completedAppointmentId: undefined,
        patient: undefined,
        appointmentType: undefined
      };
    },
    //
    applyQuickFilter(state, action: PayloadAction<string>) {
      const { payload } = action;
      state.quickFilterCode = payload;
    },
    applyEventTypeFilter(state, action: PayloadAction<string>) {
      const { payload } = action;
      state.eventType = payload;
    },
    clinicsFilterReady(state) {
      state.clinics.isReady = true;
    },
    applyClinicsFilter(state, action: PayloadAction<number[]>) {
      state.clinics.values = action.payload;
    },
    toggleShowByClinicViewMode(state) {
      state.isShowByClinicViewMode = !state.isShowByClinicViewMode;
      scheduleShowByClinicViewModeStorage.updateData(
        state.isShowByClinicViewMode
      );
    },
    toggleRaiseOnHoverViewMode(state) {
      state.raiseOnHoverViewMode = !state.raiseOnHoverViewMode;
      scheduleRaiseOnHoverStorage.updateData(state.raiseOnHoverViewMode);
    },
    toggleIncludeSupervisingProvider(state) {
      state.includeSupervisingProvider = !state.includeSupervisingProvider;
      scheduleIncludeSupervisingProviderStorage.updateData(
        state.includeSupervisingProvider
      );
    },
    disableIncludeSupervisingProvider(state) {
      state.includeSupervisingProvider = false;
      scheduleIncludeSupervisingProviderStorage.updateData(false);
    },
    toggleShowChangeColumnsOrderModal(state) {
      state.showChangeColumnsOrderModal = !state.showChangeColumnsOrderModal;
    },
    setProvidersOrder(
      state,
      action: PayloadAction<{ id: number; index: number }[]>
    ) {
      state.providersOrder = action.payload;
      providersOrderModeStorage.updateData(action.payload);
    },
    showByClinicViewMode(state) {
      state.isShowByClinicViewMode = true;
      scheduleShowByClinicViewModeStorage.updateData(true);
    },
    disableByClinicViewMode(state) {
      state.isShowByClinicViewMode = false;
      scheduleShowByClinicViewModeStorage.updateData(false);
    },
    staffFilterReady(state) {
      state.staff.isReady = true;
    },
    applyStaffFilter(
      state,
      action: PayloadAction<(MinimalStaffMember | StaffMember)[]>
    ) {
      state.staff.values = action.payload;
    },
    setSelectedFromAvailableStaff(
      state,
      action: PayloadAction<(MinimalStaffMember | StaffMember)[]>
    ) {
      state.selectedFromAvailableStaff = action.payload;
    },
    setExcludePersonalEvents(state, action: PayloadAction<boolean>) {
      state.excludePersonalEvents = action.payload;
    },
    setShowFromAllLocations(state, action: PayloadAction<boolean>) {
      state.showFromAllLocations = action.payload;
    },
    appointmentTypesFilterReady(state) {
      state.appointmentTypes.isReady = true;
    },
    applyAppointmentTypesFilter(state, action: PayloadAction<number[]>) {
      state.appointmentTypes.values = action.payload;
    },
    applyDateRangeFilter(state, action: PayloadAction<string>) {
      const dtSelectedDate = parseBackendDateOnly(
        action.payload || state.dateRange.selectedDate
      );
      const formattedDate = formatLocalDateOnly(dtSelectedDate.toISOString());

      state.dateRange.selectedDate = formattedDate;

      const { currentViewName } = state.dateRange;

      if (currentViewName === "Day") {
        state.dateRange.fromDate = formattedDate;
        state.dateRange.toDate = formattedDate;
      } else if (
        currentViewName === "Week" ||
        currentViewName === "Work Week"
      ) {
        state.dateRange.fromDate = formatLocalDateOnly(
          startOfWeek(dtSelectedDate).toISOString()
        );
        state.dateRange.toDate = formatLocalDateOnly(
          endOfWeek(dtSelectedDate).toISOString()
        );
      }
    },
    updateCurrentView(state, action: PayloadAction<SchedulerView>) {
      state.dateRange.currentViewName = action.payload;
      currentViewNameStorage.updateData(state.dateRange.currentViewName);
    },
    setCurrentViewTransient(state, action: PayloadAction<SchedulerView>) {
      state.dateRange.currentViewName = action.payload;
    },
    toggleClassicViewMode(state) {
      state.isClassicViewMode = !state.isClassicViewMode;
      scheduleViewModeStorage.updateData(state.isClassicViewMode);
    },
    toggleWorkShiftViewMode(state) {
      state.workShiftViewMode = !state.workShiftViewMode;
      workShiftViewModeStorage.updateData(state.workShiftViewMode);
    },
    setScheduleTooltip(state, action: PayloadAction<EventSearchOption | null>) {
      state.scheduleTooltip = action.payload;
    },
    applyAppointmentTagsFilter(state, action: PayloadAction<Tag[]>) {
      state.appointmentTags = action.payload;
    },
    updateStaffCellData(
      state,
      action: PayloadAction<{
        staffCount: number;
        clinicsCount: number;
        staffCellIndexes: Record<string, number>;
      }>
    ) {
      state.staffCellData = action.payload;
    },
    updateSchedulerCellWidth(
      state,
      action: PayloadAction<{
        schedulerCellContainerWidth: number;
      }>
    ) {
      state.schedulerCellContainerWidth =
        action.payload.schedulerCellContainerWidth;
    }
  }
});

export const selectFiltersPanel = (state: RootState) =>
  state.appointmentFilters.filtersPanel;

const selectQuickFilterCodeValue = (state: RootState) =>
  state.appointmentFilters.quickFilterCode;

const selectEventTypeValue = (state: RootState) =>
  state.appointmentFilters.eventType;

export const selectClinicsFilterIsReady = (state: RootState) =>
  state.appointmentFilters.clinics.isReady;
export const selectAppointmentTypesFilterIsReady = (state: RootState) =>
  state.appointmentFilters.appointmentTypes.isReady;
export const selectStaffFilterIsReady = (state: RootState) =>
  state.appointmentFilters.staff.isReady;

export const selectFiltersReady = createSelector(
  selectClinicsFilterIsReady,
  selectAppointmentTypesFilterIsReady,
  selectStaffFilterIsReady,
  (isClinicsFilterReady, isAppointmentTypesFilterReady, isStaffFilterReady) =>
    isAppointmentTypesFilterReady && isClinicsFilterReady && isStaffFilterReady
);

export const selectEditRecurringEventType = (state: RootState) =>
  state.appointmentFilters.editRecurringEventType;

export const selectPreviewVisible = (state: RootState) =>
  state.appointmentFilters.previewVisible;

export const selectScheduleTooltip = (state: RootState) =>
  state.appointmentFilters.scheduleTooltip;

export const selectFormOpen = (state: RootState) =>
  state.appointmentFilters.formOpen;

export const selectOpenFormId = (state: RootState) =>
  state.appointmentFilters.openFormId;

export const selectClinicsFilterValues = (state: RootState) =>
  state.appointmentFilters.clinics.values;

export const selectSelectedFromAvailableStaff = (state: RootState) =>
  state.appointmentFilters.selectedFromAvailableStaff;

export const selectExcludePersonalEvents = (state: RootState) =>
  state.appointmentFilters.excludePersonalEvents;

export const selectShowFromAllLocations = (state: RootState) =>
  state.appointmentFilters.showFromAllLocations;

export const selectStaffFilterValues = (state: RootState) =>
  state.appointmentFilters.staff.values;

export const selectStaffFilterValuesIds = createSelector(
  selectStaffFilterValues,
  (staffFilterValues) => staffFilterValues.map(({ id }) => id)
);

export const selectAppointmentTypesFilterValues = (state: RootState) =>
  state.appointmentFilters.appointmentTypes.values;

export const selectDateRangeValues = (state: RootState) =>
  state.appointmentFilters.dateRange;

export const selectReschedulingValues = (state: RootState) =>
  state.appointmentFilters.rescheduling;

export const selectReschedulingCarriedTags = (state: RootState) =>
  state.appointmentFilters.rescheduling.carriedAssignedTags ?? [];

export const selectPatientCreateAppointmentModeValues = (state: RootState) =>
  state.appointmentFilters.patientCreateAppointmentMode;

export const selectFollowUpSchedulingValues = (state: RootState) =>
  state.appointmentFilters.followUpScheduling;

export const selectAppointmentTagsFilterValues = (state: RootState) =>
  state.appointmentFilters.appointmentTags;

export const selectFilterValues = createSelector(
  selectEventTypeValue,
  selectQuickFilterCodeValue,
  selectClinicsFilterValues,
  selectStaffFilterValuesIds,
  selectDateRangeValues,
  selectAppointmentTagsFilterValues,
  (
    eventTypeValue,
    quickFilterCodeValue,
    clinicFilterValues,
    staffFilterValues,
    dateRangeValues,
    appointmentTagsValues
  ) => ({
    eventType: eventTypeValue,
    quickFilterCode: quickFilterCodeValue,
    clinicIds: clinicFilterValues.length ? clinicFilterValues : undefined,
    staffIds: staffFilterValues,
    fromDate: formatLocalDateOnly(
      new Date(dateRangeValues.fromDate).toISOString()
    ),
    toDate: formatLocalDateOnly(new Date(dateRangeValues.toDate).toISOString()),
    assigned_tags: appointmentTagsValues.length
      ? appointmentTagsValues.map((tag) => tag.id)
      : undefined
  })
);

export const selectIsClassicViewMode = (state: RootState) =>
  state.appointmentFilters.isClassicViewMode;

export const selectRaiseOnHoverViewMode = (state: RootState) =>
  state.appointmentFilters.raiseOnHoverViewMode;

export const selectWorkShiftViewMode = (state: RootState) =>
  state.appointmentFilters.workShiftViewMode;

export const selectIsShowByClinicViewMode = (state: RootState) =>
  state.appointmentFilters.isShowByClinicViewMode;

export const selectIncludeSupervisingProvider = (state: RootState) =>
  state.appointmentFilters.includeSupervisingProvider;

export const selectShowChangeColumnsOrderModal = (state: RootState) =>
  state.appointmentFilters.showChangeColumnsOrderModal;

export const selectProvidersOrder = (state: RootState) =>
  state.appointmentFilters.providersOrder;

export const selectStaffCellData = (state: RootState) => {
  return {
    staffCellData: state.appointmentFilters.staffCellData,
    schedulerCellContainerWidth:
      state.appointmentFilters.schedulerCellContainerWidth
  };
};

export const {
  applyQuickFilter,
  clinicsFilterReady,
  applyClinicsFilter,
  staffFilterReady,
  applyStaffFilter,
  appointmentTypesFilterReady,
  applyAppointmentTypesFilter,
  applyDateRangeFilter,
  setSelectedFromAvailableStaff,
  setExcludePersonalEvents,
  setShowFromAllLocations,
  showPreview,
  hidePreview,
  toggleClassicViewMode,
  toggleFiltersPanel,
  filtersPanelStopAnimation,
  hideFiltersPanel,
  toggleShowByClinicViewMode,
  toggleRaiseOnHoverViewMode,
  toggleIncludeSupervisingProvider,
  disableIncludeSupervisingProvider,
  showByClinicViewMode,
  disableByClinicViewMode,
  toggleWorkShiftViewMode,
  setEditRecurringEventType,
  openForm,
  closeForm,
  collapseReschedulingForm,
  expandReschedulingForm,
  startRescheduling,
  endRescheduling,
  updateReschedulingReturnToPath,
  updateReschedulingData,
  removeCarriedReschedulingTag,
  updateCurrentView,
  setCurrentViewTransient,
  setScheduleTooltip,
  toggleShowChangeColumnsOrderModal,
  setProvidersOrder,
  applyAppointmentTagsFilter,
  collapsePatientModeForm,
  expandPatientModeForm,
  startPatientCreateAppointmentMode,
  endPatientCreateAppointmentMode,
  startFollowUpScheduling,
  endFollowUpScheduling,
  updateStaffCellData,
  updateSchedulerCellWidth
} = schedulerSlice.actions;

export default schedulerSlice.reducer;
